import { useState } from 'react';
import axios from 'axios';
import { X, Star, Send, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LeaveReviewDialog = ({ open, onClose }) => {
  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setName('');
    setRating(0);
    setHoverRating(0);
    setComment('');
    setError(null);
    setSuccess(false);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) return setError('Merci d\'indiquer votre prénom.');
    if (rating < 1) return setError('Merci de choisir une note (1 à 5 étoiles).');
    if (!comment.trim()) return setError('Merci d\'écrire quelques mots.');

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/reviews`, {
        name: name.trim(),
        rating,
        comment: comment.trim(),
      });
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Une erreur est survenue. Merci de réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={handleClose}
      data-testid="leave-review-dialog"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 transition-colors"
          aria-label="Fermer"
          data-testid="close-review-dialog"
        >
          <X size={20} className="text-slate-600" />
        </button>

        {success ? (
          <div className="text-center py-4">
            <CheckCircle2 size={56} className="mx-auto mb-4 text-emerald-500" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Merci pour votre avis !</h3>
            <p className="text-slate-600 leading-relaxed">
              Votre témoignage a bien été reçu. Il sera relu et publié prochainement.
            </p>
            <button
              onClick={handleClose}
              className="mt-6 px-6 py-2.5 rounded-full text-white font-medium"
              style={{ backgroundColor: '#0EA5E9' }}
              data-testid="review-success-close"
            >
              Fermer
            </button>
          </div>
        ) : (
          <>
            <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-1">
              Laisser un avis
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              Votre témoignage sera publié après relecture.
            </p>

            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Prénom
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={80}
                  placeholder="Ex : Marie"
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition"
                  data-testid="review-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Note
                </label>
                <div className="flex gap-1" data-testid="review-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      onMouseEnter={() => setHoverRating(n)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-1 transition-transform hover:scale-110"
                      aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
                      data-testid={`star-${n}`}
                    >
                      <Star
                        size={32}
                        className={
                          n <= (hoverRating || rating)
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-slate-300'
                        }
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Votre témoignage
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  maxLength={2000}
                  rows={5}
                  placeholder="Partagez votre expérience..."
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition resize-none"
                  data-testid="review-comment-input"
                />
                <p className="text-xs text-slate-400 mt-1 text-right">{comment.length}/2000</p>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="review-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-white font-semibold shadow-md transition-all hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#0EA5E9' }}
                data-testid="submit-review-btn"
              >
                <Send size={16} />
                {submitting ? 'Envoi…' : 'Envoyer mon avis'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default LeaveReviewDialog;
